import 'braid-design-system/reset';
import { HirerAuthProvider, useHirerAuth } from '@seek/hirer-auth-react';
import { HirerApolloProvider } from '@seek/hirer-graphql-react';
import { UivBanner, useIsNVLEnabled } from '@seek/uiv-banner';
import { VocabProvider } from '@vocab/react';
import { BraidProvider, Page } from 'braid-design-system';
import apacTheme from 'braid-design-system/themes/apac';
import seekJobs from 'braid-design-system/themes/seekJobs';
import type React from 'react';
import { Helmet } from 'react-helmet';
import { Route, Routes } from 'react-router-dom';
import type { SharedRenderProps } from 'sku';
import { v4 } from 'uuid';

import { Authenticate } from 'src/App/context/Authentication/Authenticate';
import { localizeRoutesForSite } from 'src/routes';

import type { EmployerSiteName } from '../types';

import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { ErrorSectionWithLayout } from './components/ErrorSection/ErrorSectionWithLayout';
import { Header, Footer } from './components/HeaderFooter/HeaderFooter';
import { AppContextProvider } from './context/AppContext';
import { TrackingProvider } from './context/TrackingProvider';
import { useQueryAccountInfo } from './hooks/useQueryAccountInfo';
import { AdBudgetsPage } from './pages/ad-budget-selection/AdBudgetsPage';
import { LoadingBlock } from './pages/ad-budget-selection/components/Loading/Loading.tsx';
import CheckoutPage from './pages/checkout/CheckoutPage';
import type { Environment } from './services/environment';
import { createAnalyticsProvider } from './utils/analytics/initiaseTealiumAdaptor';
import { getLogger, initialiseLogger } from './utils/logger/logger';
import { getWindowLocalEnv } from './utils/window.ts';

// TODO: support initialisation of build version during local running and fix naming build version vs number
type AppProps = Pick<SharedRenderProps, 'language'> & {
  buildVersion: string;
  branchName: string;
  environment: Environment;
  site: EmployerSiteName;
};

export const App: React.FC<React.PropsWithChildren<AppProps>> = ({
  buildVersion,
  branchName,
  environment,
  language,
  site,
}) => {
  const { accountInfo, loading: accountLoading } = useQueryAccountInfo();
  const { loading: authLoading } = useHirerAuth();
  const loading = authLoading || accountLoading;
  const currentAdvertiserId = accountInfo?.currentAdvertiser?.advertiserId;
  const currentUserId = accountInfo?.user?.id;

  const isLocalAuth = Boolean(
    environment === 'local' && getWindowLocalEnv()?.localAuth,
  );

  getLogger().configureApplicationContext({
    advertiserId: currentAdvertiserId,
    userId: currentUserId,
  });

  const siteRoutes = localizeRoutesForSite(site);

  const routeNameToPage: Record<string, React.ReactNode> = {
    adBudget: <AdBudgetsPage uuid={v4()} />,
    checkout: (
      <Authenticate level={isLocalAuth ? 'Mocked' : 'Hirer'}>
        <CheckoutPage />
      </Authenticate>
    ),
  };

  const analyticsClient = createAnalyticsProvider();

  const renderComponent = loading ? (
    <LoadingBlock />
  ) : (
    <Routes>
      {siteRoutes.map((route) => {
        const componentRender = routeNameToPage[route.name];
        return (
          <Route
            path={route.route}
            key={route.route}
            element={componentRender}
          />
        );
      })}
    </Routes>
  );

  const headerFooterProps = { site, environment, language };

  return (
    <ErrorBoundary errorDisplay={<ErrorSectionWithLayout />}>
      <TrackingProvider analyticsClient={analyticsClient}>
        <Page footer={<Footer {...headerFooterProps} />}>
          <UivBanner
            applicationName="hirer-product-purchase-ui"
            branch={branchName}
            version={buildVersion}
          />
          <Header {...headerFooterProps} />
          {renderComponent}
        </Page>
      </TrackingProvider>
    </ErrorBoundary>
  );
};

const AppWrapper: React.FC<React.PropsWithChildren<AppProps>> = (props) => {
  const { environment, language, buildVersion, site } = props;
  const isLocalAuth = environment === 'local' && getWindowLocalEnv()?.localAuth;
  const stagingHirerGraph = 'https://talent.staging.seek.com.au/graphql';
  const uri = environment === 'local' ? stagingHirerGraph : undefined;
  const isNVLEnabled = useIsNVLEnabled(false);

  initialiseLogger({ environment, buildVersion });

  return (
    <VocabProvider language={language}>
      <HirerAuthProvider authenticationLevel={isLocalAuth ? 'Mocked' : 'None'}>
        <HirerApolloProvider
          appName="hirer-product-purchase-ui"
          site={site}
          uri={uri}
        >
          <BraidProvider theme={isNVLEnabled ? seekJobs : apacTheme}>
            <Helmet>
              <title>SEEK - Pre-pay and save</title>
              <html lang={language} />
              <meta
                name="description"
                content="SEEK Pre-pay and save. Pay upfront and secure a discount for 12 months, with the flexibility to use your Ad Budget across different ad types."
              />
            </Helmet>
            <AppContextProvider
              site={site}
              environment={environment}
              language={language}
            >
              <App {...props} />
            </AppContextProvider>
          </BraidProvider>
        </HirerApolloProvider>
      </HirerAuthProvider>
    </VocabProvider>
  );
};

export default AppWrapper;
